import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SectionHeader from './SectionHeader';

const ProgressSlider = ({ image1, image2 }) => {
    const [isHovered, setIsHovered] = useState(false);
    const { t } = useTranslation();

    return (
        <div
            className="relative w-full h-56 sm:h-80 rounded-lg overflow-hidden cursor-ew-resize group shadow-lg"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <img src={image1} alt={t('a11y.blueprintAlt')} className="absolute inset-0 w-full h-full object-cover" />

            <motion.div
                className="absolute inset-y-0 right-0 overflow-hidden"
                initial={{ width: "50%" }}
                animate={{ width: isHovered ? "100%" : "50%" }}
                transition={{ duration: 0.45, ease: "easeInOut" }}
            >
                <img src={image2} alt={t('a11y.progressAlt')} className="absolute top-0 right-0 w-[200%] h-full object-cover max-w-none" style={{ width: '200%' }} />
            </motion.div>

            {/* Divider Line */}
            <motion.div
                className="absolute inset-y-0 bg-gold-sun w-1 z-10"
                initial={{ right: "50%" }}
                animate={{ right: isHovered ? "100%" : "50%" }}
                transition={{ duration: 0.45, ease: "easeInOut" }}
            >
                <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-gold-sun rounded-full flex items-center justify-center text-coal text-xs">
                    ↔
                </div>
            </motion.div>
        </div>
    );
};

const Progress = () => {
    const { t } = useTranslation();
    return (
        <section id="progress" className="py-14 md:py-28 bg-paper/50 relative">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

                <SectionHeader
                    index={4}
                    title={t('progress.title')}
                    subtitle={t('progress.subtitle')}
                />

                <div className="space-y-14 md:space-y-32">

                    {/* Phase 1 */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center text-center lg:text-start"
                    >
                        <div className="space-y-4 md:space-y-6 order-2 lg:order-1">
                            <span className="text-gold-deep font-bold tracking-widest text-xs md:text-sm uppercase inline-block bg-gold-sun/10 px-3 py-1 rounded-full">{t('progress.phase1Badge')}</span>
                            <h3 className="text-2xl md:text-3xl font-bold text-coal">{t('progress.phase1Title')}</h3>
                            <p className="text-base md:text-lg text-stone-600 leading-relaxed font-medium max-w-lg mx-auto lg:mx-0">
                                {t('progress.phase1Desc')}
                            </p>
                        </div>
                        <div className="order-1 lg:order-2 px-4 sm:px-8 lg:px-0">
                            <ProgressSlider image1="/images/school-blueprint.png" image2="/images/workers-progress.png" />
                        </div>
                    </motion.div>

                    {/* Phase 2: Active Phase */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center lg:flex-row-reverse text-center lg:text-start"
                    >
                        <div className="order-1 lg:order-1 relative group rounded-lg border border-gold-sun/50 p-1 mx-4 sm:mx-8 lg:mx-0">
                            <div className="rounded-md overflow-hidden shadow-xl relative bg-white">
                                <img src="/images/school-workshops.png" alt={t('a11y.workshopsAlt')} className="w-full h-56 sm:h-80 object-cover" />
                                {/* Status chip: one calm "live" cue — a small pulsing dot, nothing else moves */}
                                <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/95 text-gold-deep px-3 py-1.5 rounded-full text-xs font-bold shadow-sm">
                                    <span className="relative flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-sun opacity-60"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-gold-deep"></span>
                                    </span>
                                    {t('progress.phase2ActiveBadge')}
                                </div>
                            </div>
                        </div>
                        <div className="space-y-4 md:space-y-6 order-2 lg:order-2">
                            <span className="text-gold-deep font-bold tracking-widest text-xs md:text-sm uppercase inline-block bg-gold-sun/10 px-3 py-1 rounded-full">{t('progress.phase2Badge')}</span>
                            <h3 className="text-2xl md:text-3xl font-bold text-coal">{t('progress.phase2Title')}</h3>
                            <p className="text-base md:text-lg text-stone-600 leading-relaxed font-medium max-w-lg mx-auto lg:mx-0">
                                {t('progress.phase2Desc')}
                            </p>
                        </div>
                    </motion.div>

                    {/* Phase 3 */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        className="space-y-12 md:space-y-16"
                    >
                        <div className="text-center max-w-2xl mx-auto space-y-4 px-4">
                            <span className="text-stone-500 font-bold tracking-widest text-xs md:text-sm uppercase inline-block bg-stone-500/10 px-3 py-1 rounded-full">{t('progress.phase3Badge')}</span>
                            <h3 className="text-2xl md:text-3xl font-bold text-coal">{t('progress.phase3Title')}</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 px-4 sm:px-8 lg:px-0">

                            {/* Dorms Blueprint Hover */}
                            <div className="col-span-1 border border-stone-200/50 bg-white/30 backdrop-blur p-4 rounded-lg group overflow-hidden relative min-h-[210px] md:min-h-[400px]">
                                <img src="/images/dorms.png" alt={t('a11y.dormsRenderAlt')} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300" />
                                <img src="/images/dorms-blueprint.png" alt={t('a11y.dormsBlueprintAlt')} className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-multiply" />
                                <div className="absolute bottom-0 inset-x-0 h-2/3 bg-gradient-to-t from-coal/90 via-coal/40 to-transparent p-6 flex flex-col justify-end">
                                    <h4 className="text-white font-bold text-lg md:text-xl drop-shadow-md mb-1">{t('progress.dormTitle')}</h4>
                                    <p className="text-white/80 text-sm drop-shadow-md leading-relaxed">{t('progress.dormDesc')}</p>
                                </div>
                            </div>

                            {/* Video 1: Water */}
                            <div className="col-span-1 border border-stone-200/50 bg-white/30 backdrop-blur p-4 rounded-lg relative overflow-hidden min-h-[210px] md:min-h-[400px]">
                                <video src="/images/water-station.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute bottom-0 inset-x-0 h-2/3 bg-gradient-to-t from-ink-900/90 via-ink-900/40 to-transparent p-6 flex flex-col justify-end">
                                    <h4 className="text-white font-bold text-lg md:text-xl mb-1 md:mb-2">{t('progress.waterTitle')}</h4>
                                    <p className="text-stone-200 text-xs md:text-sm">{t('progress.waterDesc')}</p>
                                </div>
                            </div>

                            {/* Video 2: Solar */}
                            <div className="col-span-1 border border-stone-200/50 bg-white/30 backdrop-blur p-4 rounded-lg relative overflow-hidden min-h-[210px] md:min-h-[400px]">
                                <video src="/images/solar-station.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" />
                                <div className="absolute bottom-0 inset-x-0 h-2/3 bg-gradient-to-t from-ink-900/90 via-ink-900/40 to-transparent p-6 flex flex-col justify-end">
                                    <h4 className="text-white font-bold text-lg md:text-xl mb-1 md:mb-2">{t('progress.solarTitle')}</h4>
                                    <p className="text-stone-200 text-xs md:text-sm">{t('progress.solarDesc')}</p>
                                </div>
                            </div>

                        </div>
                    </motion.div>

                </div>
                
                {/* Missing Facilities Section */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="mt-24 md:mt-32 bg-ink-800 text-paper p-8 md:p-12 rounded-lg shadow-2xl relative overflow-hidden"
                >
                    <div className="absolute inset-0 z-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 10% 20%, rgba(16, 185, 129, 0.4) 0%, transparent 40%)' }}></div>
                    <div className="relative z-10 max-w-4xl mx-auto space-y-6 text-start">
                        <span className="text-gold-sun font-bold tracking-widest text-xs md:text-sm uppercase inline-block bg-gold-sun/10 px-3 py-1 rounded-full">
                            {t('missingFacilities.title')}
                        </span>
                        <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                            {t('missingFacilities.desc')}
                        </h3>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5 md:gap-y-4 pt-5">
                            {(t('missingFacilities.list', { returnObjects: true }) || []).map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3 text-stone-200 text-sm md:text-lg">
                                    <span className="mt-2.5 h-1.5 w-1.5 rotate-45 bg-gold-sun shrink-0"></span>
                                    <span className="flex-1">{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </motion.div>

            </div>
        </section>
    );
};

export default Progress;
